if (!customElements.get('bulk-variant')) {
  customElements.define(
    'bulk-variant',
    class BulkVariant extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.caseQty = parseInt(this.dataset.caseQty, 10) || 0;
        this.packQty = parseInt(this.dataset.packQty, 10) || 0;
        this.bulkCases = parseInt(this.dataset.bulkCases, 10) || 25;
        this.bulkImage = this.dataset.bulkImage;
        /* Shopify's variant JSON has no inventory_quantity, so Liquid hands us a
         * map of case variant id -> can this SKU cover a 25 case order. */
        try {
          this.bulkStock = JSON.parse(this.dataset.bulkStock || '{}');
        } catch (e) {
          this.bulkStock = {};
        }
        this.orderByPosition = parseInt(this.dataset.orderByPosition, 10) || 1;
        this.bulkMode = false;

        this.variantRadios = document.getElementById(`variant-radios-${this.sectionId}`);
        this.priceContainer = document.getElementById(`price-${this.sectionId}`);
        this.bulkBuy = document.querySelector('[data-bulk-buy]');

        this.moveBulkPill();
        this.bindEvents();
        this.watchPriceBlock();
        this.runSync();
      }

      /* The BULK pill is rendered outside <variant-radios> so it never joins the
       * real option set; visually it belongs at the end of the "Order by" row. */
      moveBulkPill() {
        const source = document.querySelector('.bulk-variant-pill-source');
        const row = document.querySelector('.bulk-variant-order-by .bulk-variant-pills');
        if (!source || !row) return;

        this.bulkPill = source.querySelector('[data-bulk-pill]');
        if (this.bulkPill) row.appendChild(this.bulkPill);
        source.remove();
      }

      bindEvents() {
        if (this.bulkPill) {
          this.bulkPill.addEventListener('click', () => this.enterBulkMode());
        }

        if (!this.variantRadios) return;

        /* Leaving bulk is driven by `click`, not `change`. Entering bulk already
         * checks the Case radio, so clicking "Case of N" afterwards changes
         * nothing and fires no change event -- listening for change alone left
         * the shopper stuck in bulk mode with no way back to the case. */
        this.variantRadios.addEventListener('click', (event) => {
          if (this.suppressExit) return;
          if (event.target.closest('[data-bulk-pill]')) return;
          if (!event.target.closest('.bulk-variant-order-by')) return;
          this.exitBulkMode();
        });

        /* Keyboard: arrow keys move a radio group without a click. */
        this.variantRadios.addEventListener('change', (event) => {
          if (this.suppressExit) return;
          if (!event.target.closest || !event.target.closest('.bulk-variant-order-by')) return;
          this.exitBulkMode();
        });
      }

      /* variant-radios replaces the price block wholesale after each change. */
      watchPriceBlock() {
        if (!this.priceContainer) return;
        this.observer = new MutationObserver(() => this.runSync());
        this.startObserving();
      }

      startObserving() {
        if (!this.observer || !this.priceContainer) return;
        this.observer.observe(this.priceContainer, { childList: true, subtree: true });
      }

      /* Every DOM write goes through here. The observer watches the price block
       * and the sync methods write into the price block, so running them while
       * observing would retrigger the observer forever and hang the page. The
       * observer is detached for the duration and its queue drained before it is
       * reattached, so only genuine variant re-renders wake it. */
      runSync() {
        if (this.syncing) return;
        this.syncing = true;
        if (this.observer) this.observer.disconnect();
        try {
          this.syncPills();
          this.syncBulkVariant();
          if (this.bulkMode) this.paintBulkMode();
        } finally {
          if (this.observer) this.observer.takeRecords();
          this.startObserving();
          this.syncing = false;
        }
      }

      variantData() {
        if (this.variants) return this.variants;
        if (!this.variantRadios) return [];
        const json = this.variantRadios.querySelector('script[type="application/json"]');
        if (!json) return [];
        try {
          this.variants = JSON.parse(json.textContent);
        } catch (e) {
          this.variants = [];
        }
        return this.variants;
      }

      currentOptions() {
        if (!this.variantRadios) return [];
        return Array.from(this.variantRadios.querySelectorAll('fieldset')).map((fieldset) => {
          const checked = Array.from(fieldset.querySelectorAll('input')).find((input) => input.checked);
          return checked ? checked.value : null;
        });
      }

      /* The Case variant matching every currently selected option except "Order by". */
      caseVariant() {
        const options = this.currentOptions();
        const index = this.orderByPosition - 1;
        return this.variantData().find((variant) => {
          if (!variant.options[index] || variant.options[index].indexOf('Case of') === -1) return false;
          return variant.options.every((value, i) => i === index || value === options[i]);
        });
      }

      /* Baseline for "Save N% vs Singles", falling back to the pack unit price
       * on products tagged "packs", which have no Singles variant. */
      singleUnitPrice() {
        const options = this.currentOptions();
        const index = this.orderByPosition - 1;
        const match = (needle) =>
          this.variantData().find((variant) => {
            if (!variant.options[index] || variant.options[index].indexOf(needle) === -1) return false;
            return variant.options.every((value, i) => i === index || value === options[i]);
          });

        const single = match('Single');
        if (single) return single.price;

        const pack = match('Pack of');
        if (pack && this.packQty > 0) return Math.round(pack.price / this.packQty);
        return 0;
      }

      /* A variant change only re-renders the price block server side, so the
       * per-pill unit prices are refreshed here for the new Color / Shape. */
      syncPills() {
        if (!this.variantRadios) return;
        const options = this.currentOptions();
        const index = this.orderByPosition - 1;
        const baseline = this.singleUnitPrice();

        this.variantRadios.querySelectorAll('.bulk-variant-pill__input').forEach((input) => {
          const variant = this.variantData().find((candidate) => {
            if (candidate.options[index] !== input.value) return false;
            return candidate.options.every((value, i) => i === index || value === options[i]);
          });

          const label = input.nextElementSibling;
          if (!variant || !label) return;

          const qty = this.unitsFor(input.value);
          const unit = Math.round(variant.price / qty);

          const priceEl = label.querySelector('.bulk-variant-pill__price');
          if (priceEl) priceEl.innerHTML = `${this.money(unit)} <span class="bulk-variant-pill__price_ea">ea</span>`;

          const saveEl = label.querySelector('.bulk-variant-pill__save');
          if (saveEl) {
            const save = baseline > 0 && unit < baseline ? Math.floor(((baseline - unit) * 100) / baseline) : 0;
            saveEl.textContent = save > 0 ? `${save}% off` : '';
          }

          input.classList.toggle('disabled', !variant.available);
        });
      }

      /* Units behind one "Order by" value, so pill prices read as price each. */
      unitsFor(value) {
        if (value.indexOf('Case of') !== -1 && this.caseQty > 0) return this.caseQty;
        if (value.indexOf('Pack of') !== -1 && this.packQty > 0) return this.packQty;
        return 1;
      }

      /* Keep the bulk form and its price in step with Color / Shape changes. */
      syncBulkVariant() {
        const variant = this.caseVariant();
        if (!variant) {
          /* The newly chosen Color / Shape has no Case variant, so there is
             nothing a bulk order could be built from. */
          if (this.bulkPill) this.bulkPill.hidden = true;
          if (this.bulkMode) this.exitBulkMode();
          return;
        }
        if (this.bulkPill) this.bulkPill.hidden = false;
        if (!this.bulkBuy) return;

        const idInput = this.bulkBuy.querySelector('[data-bulk-variant-id]');
        if (idInput) idInput.value = variant.id;

        const total = Math.round((variant.price * this.bulkCases * 90) / 100);
        const pcs = this.caseQty * this.bulkCases;
        const unit = pcs > 0 ? Math.round(total / pcs) : 0;        

        this.setText('[data-bulk-submit-price]', this.money(total));
        this.setText('[data-bulk-total]', this.money(total));
        this.setText('[data-bulk-unit]', this.money(unit));
        // this.setText('[data-bulk-pcs]', String(pcs));
        this.setText('[data-bulk-pcs]', Number(pcs).toLocaleString());

        const single = this.singleUnitPrice();
        if (single > 0 && unit > 0 && unit < single) {
          this.setText('[data-bulk-save]', String(Math.floor(((single - unit) * 100) / single)));
        }

        this.updateBulkAvailability(variant);
      }

      /* Spec: under 25 cases of stock, grey the button out with placeholder text. */
      updateBulkAvailability(variant) {
        const available = variant.available && this.bulkStock[variant.id] !== false;
        const button = this.bulkBuy && this.bulkBuy.querySelector('.bulk-variant-buy__submit');
        if (!button) return;

        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));

        /* Two labels rendered by Liquid, toggled here. Rebuilding innerHTML would
         * destroy the price span this class writes into on every sync. */
        const yes = button.querySelector('[data-bulk-label-available]');
        const no = button.querySelector('[data-bulk-label-unavailable]');
        if (yes) yes.hidden = !available;
        if (no) no.hidden = available;

        if (this.bulkPill) this.bulkPill.classList.toggle('bulk-variant-pill--unavailable', !available);
      }

      enterBulkMode() {
        /* Move the real radio group onto the Case value first, so the variant
         * Shopify thinks is selected is the one bulk actually orders. */
        const variant = this.caseVariant();
        if (variant && this.variantRadios) {
          const index = this.orderByPosition - 1;
          const target = Array.from(this.variantRadios.querySelectorAll('.bulk-variant-pill__input')).find(
            (input) => input.value === variant.options[index]
          );
          if (target && !target.checked) {
            this.suppressExit = true;
            target.checked = true;
            this.variantRadios.dispatchEvent(new Event('change', { bubbles: true }));
            this.suppressExit = false;
          }
        }

        this.bulkMode = true;
        this.paintBulkMode();
      }

      exitBulkMode() {
        if (!this.bulkMode) return;
        this.bulkMode = false;
        this.paintBulkMode();
      }

      paintBulkMode() {
        const on = this.bulkMode;

        document.body.classList.toggle('bulk-variant-mode', on);
        if (this.bulkPill) {
          this.bulkPill.classList.toggle('bulk-variant-pill--checked', on);
          this.bulkPill.setAttribute('aria-pressed', String(on));
        }

        /* Price block: bulk row replaces whichever row Liquid marked active. */
        const price = document.querySelector('[data-bulk-price]');
        if (price) {
          price.classList.toggle('bulk-variant-price--bulk-mode', on);
        }

        /* Buy area: bulk form instead of qty selector + standard add to cart. */
        const quantity = document.getElementById(`Quantity-Form-${this.sectionId}`);
        const standardForm = document.querySelector(`#product-form-${this.sectionId}`);
        const standardWrapper = standardForm ? standardForm.closest('product-form') : null;

        if (quantity) quantity.hidden = on;
        if (standardWrapper) standardWrapper.hidden = on;
        if (this.bulkBuy) this.bulkBuy.hidden = !on;

        this.swapGalleryImage(on);
        this.updateOrderByLabel(on);
      }

      /* Spec: selecting BULK always shows the bulk artwork from the Shopify CDN. */
      swapGalleryImage(on) {
        if (!this.bulkImage) return;

        let slot = document.querySelector('[data-bulk-image-slot]');
        if (on && !slot) {
          const gallery = document.querySelector(`[id^="MediaGallery-${this.sectionId}"]`) || document.querySelector('.product__media-wrapper');
          if (!gallery || !gallery.parentNode) return;
          slot = document.createElement('div');
          slot.className = 'bulk-variant-image';
          slot.setAttribute('data-bulk-image-slot', '');
          slot.innerHTML = `<img src="${this.bulkImage}" alt="Bulk pricing: 25+ cases" loading="lazy">`;
          /* Sibling, not child -- the gallery itself gets hidden below. */
          gallery.parentNode.insertBefore(slot, gallery);
        }
        if (slot) slot.hidden = !on;
        document.querySelectorAll(`[id^="MediaGallery-${this.sectionId}"]`).forEach((el) => {
          el.classList.toggle('bulk-variant-media-hidden', on);
        });
      }

      updateOrderByLabel(on) {
        const label = document.querySelector('[data-bulk-order-by-label="true"]');
        if (!label) return;

        if (on) {
          label.textContent = `Bulk (${this.bulkCases} Cases)`;
          return;
        }

        /* Read the live selection instead of restoring a cached string. Entering
         * bulk switches the underlying option to the case, so a label captured on
         * the way in is stale by the time bulk is left -- it showed "Singles"
         * next to a selected "Case of 25". */
        if (!this.variantRadios) return;
        const checked = Array.from(this.variantRadios.querySelectorAll('.bulk-variant-pill__input')).find(
          (input) => input.checked
        );
        if (checked) label.textContent = checked.value;
      }

      /* Never write a value that is already there -- redundant writes are still
       * mutations, and cost a full observer cycle each. */
      setText(selector, value) {
        document.querySelectorAll(selector).forEach((el) => {
          if (el.textContent !== value) el.textContent = value;
        });
      }

      money(cents) {
        if (window.Shopify && window.Shopify.formatMoney && window.themeMoneyFormat) {
          return window.Shopify.formatMoney(cents, window.themeMoneyFormat);
        }
        return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }
  );
}
